/**
 * TypeScript types for notification preferences.
 */

export type DigestThreshold = 'overdue_only' | 'due_today' | 'include_upcoming';
export type NotificationChannel = 'email' | 'telegram';
export type NotificationStatus = 'sent' | 'failed' | 'bounced' | 'blocked';

export interface NotificationPreferences {
  id: string;
  user_id: string;
  enabled: boolean;
  preferred_time: string; // HH:MM:SS format
  digest_threshold: DigestThreshold;
  email_enabled: boolean;
  telegram_enabled: boolean;
  telegram_chat_id: string | null;
  setup_completed: boolean;
  setup_prompt_count: number;
  // Error tracking
  email_error_message: string | null;
  email_disabled_at: string | null;
  telegram_error_message: string | null;
  telegram_disabled_at: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Check if a notification channel has been disabled due to an error.
 * Returns true when the channel is disabled AND has a stored error message.
 */
export function hasChannelError(
  preferences: NotificationPreferences,
  channel: 'email' | 'telegram'
): boolean {
  if (channel === 'email') {
    return !preferences.email_enabled && !!preferences.email_error_message;
  }
  return !preferences.telegram_enabled && !!preferences.telegram_error_message;
}

export interface NotificationPreferencesUpdate {
  enabled?: boolean;
  preferred_time?: string;
  digest_threshold?: DigestThreshold;
  email_enabled?: boolean;
  telegram_enabled?: boolean;
  setup_completed?: boolean;
}

export interface TelegramLinkResponse {
  link_code: string;
  expires_at: string;
  bot_username: string;
  deep_link: string;
}

export interface TelegramStatusResponse {
  linked: boolean;
  chat_id_masked: string | null;
}

export interface NotificationLogEntry {
  id: string;
  sent_at: string;
  channel: NotificationChannel;
  task_count: number;
  status: NotificationStatus;
  error_message: string | null;
}

export interface NotificationLogResponse {
  entries: NotificationLogEntry[];
  total: number;
}

// Labels for UI display
export const DIGEST_THRESHOLD_LABELS: Record<DigestThreshold, string> = {
  overdue_only: 'Only when tasks are overdue',
  due_today: 'Tasks due today or overdue',
  include_upcoming: 'Include upcoming week',
};

export const TIME_PRESETS = [
  { value: '08:00:00', label: '8am' },
  { value: '14:00:00', label: '2pm' },
  { value: '18:00:00', label: '6pm' },
] as const;
